import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import type { CreditRangeEnum } from '@cc-shopping/contracts/credit-cards-page/roundup/utils';
import {
  CARD_FEATURES_OPTIONS_LABEL_MAP,
  CREDIT_RANGE_OPTIONS_LABEL_MAP,
  SORT_BY_OPTIONS_LABEL_MAP,
} from '../config';
import { FilterOffersByType, FilterType, SortOffersByType } from './enums';
import type { AvailableOffersCounts, FilterState, SelectedNonDefaultFilters } from './types';

export const DEFAULT_CARD_FEATURES = FilterOffersByType.ALL_CARDS;
export const DEFAULT_SORT_BY = SortOffersByType.FEATURED;

const getOfferIdsForCardFeatureFilter = ({
  allOfferIds,
  noAnnualFeeOfferIds,
  introOfferOfferIds,
  selectedCardFeatures,
}: {
  allOfferIds: Set<string>;
  noAnnualFeeOfferIds: Set<string>;
  introOfferOfferIds: Set<string>;
  selectedCardFeatures: FilterOffersByType;
}) => {
  const offerIdsForCardFeatureFilter = new Set<string>();
  allOfferIds.forEach(offerId => {
    if (selectedCardFeatures === FilterOffersByType.ALL_CARDS) {
      offerIdsForCardFeatureFilter.add(offerId);
    } else if (
      selectedCardFeatures === FilterOffersByType.NO_ANNUAL_FEE &&
      noAnnualFeeOfferIds.has(offerId)
    ) {
      offerIdsForCardFeatureFilter.add(offerId);
    } else if (
      selectedCardFeatures === FilterOffersByType.HAS_INTRO_OFFER &&
      introOfferOfferIds.has(offerId)
    ) {
      offerIdsForCardFeatureFilter.add(offerId);
    }
  });
  return offerIdsForCardFeatureFilter;
};

const getOfferIdsForCardFeatureAndIssuerFilter = ({
  offerIdsForCardFeatureFilter,
  selectedIssuers,
  offersByIssuerMap,
}: {
  offerIdsForCardFeatureFilter: Set<string>;
  selectedIssuers: string[];
  offersByIssuerMap: Record<string, Set<string>>;
}) => {
  const offerIdsForCardFeatureAndIssuerFilter = new Set<string>();
  if (selectedIssuers.length === 0) {
    // No issuer selected, so all offers are included
    return offerIdsForCardFeatureFilter;
  } else {
    selectedIssuers.forEach(issuerId => {
      const issuerOffersIds = offersByIssuerMap[issuerId];
      issuerOffersIds.forEach(offerId => {
        const includedOffer = offerIdsForCardFeatureFilter.has(offerId);
        if (includedOffer) {
          offerIdsForCardFeatureAndIssuerFilter.add(offerId);
        }
      });
    });
  }
  return offerIdsForCardFeatureAndIssuerFilter;
};

const getOfferIdsForCardFeatureAndCreditRangeFilter = ({
  offerIdsForCardFeatureFilter,
  selectedCreditRangeFilter,
  offersByRCSMap,
}: {
  offerIdsForCardFeatureFilter: Set<string>;
  selectedCreditRangeFilter: CreditRangeEnum[];
  offersByRCSMap: Record<CreditRangeEnum, Set<string>>;
}) => {
  const offerIdsForCardFeatureAndCreditRangeFilter = new Set<string>();
  if (selectedCreditRangeFilter.length === 0) {
    // No credit range selected, so all offers are included
    return offerIdsForCardFeatureFilter;
  }
  offerIdsForCardFeatureFilter.forEach(offerId => {
    selectedCreditRangeFilter.forEach(creditRange => {
      if (offersByRCSMap[creditRange]?.has(offerId)) {
        offerIdsForCardFeatureAndCreditRangeFilter.add(offerId);
      }
    });
  });
  return offerIdsForCardFeatureAndCreditRangeFilter;
};

export const cleanupFiltersAndGetAvailableOffersCount = ({
  preComputedFilterOfferIds,
  newSelectedFilters,
  filtersToCleanup = { issuerIdsToCleanup: [], creditRangesToCleanup: [] },
}: {
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
  newSelectedFilters: FilterState;
  filtersToCleanup?: {
    issuerIdsToCleanup: string[];
    creditRangesToCleanup: CreditRangeEnum[];
  };
}): { availableOffersCount: AvailableOffersCounts; cleanedFilters: FilterState } => {
  const {
    allOfferIds,
    defaultCreditRangesCount,
    defaultIssuersCount,
    offersByIssuerMap,
    offersByRCSMap,
    noAnnualFeeOfferIds,
    introOfferOfferIds,
  } = preComputedFilterOfferIds;
  const allIssuerIds = Object.keys(offersByIssuerMap);
  const availableOffersCount: {
    issuers: Record<string, number>;
    creditRange: Record<CreditRangeEnum, number>;
  } = {
    issuers: { ...defaultIssuersCount },
    creditRange: { ...defaultCreditRangesCount },
  };

  // Cleanup selected filters that do not have any offers to show
  const cleanedFilters = { ...newSelectedFilters };
  const { issuerIdsToCleanup, creditRangesToCleanup } = filtersToCleanup;

  if (issuerIdsToCleanup.length > 0 || creditRangesToCleanup.length > 0) {
    cleanedFilters.issuerFilter = cleanedFilters.issuerFilter.filter(
      id => !issuerIdsToCleanup.includes(id),
    );
    filtersToCleanup.issuerIdsToCleanup = [];
    cleanedFilters.creditRangeFilter = cleanedFilters.creditRangeFilter.filter(
      range => !creditRangesToCleanup.includes(range),
    );
    filtersToCleanup.creditRangesToCleanup = [];
  }

  const selectedCardFeatures = newSelectedFilters.cardFeatureFilter;
  const selectedCreditRangeFilter = newSelectedFilters.creditRangeFilter;
  const selectedIssuers = newSelectedFilters.issuerFilter;

  // Included offers in selected Card Feature filter
  const offerIdsForCardFeatureFilter = getOfferIdsForCardFeatureFilter({
    allOfferIds,
    noAnnualFeeOfferIds,
    introOfferOfferIds,
    selectedCardFeatures,
  });

  // Included offers in selected both Card Feature and Issuer filters
  const offerIdsForCardFeatureAndIssuerFilter = getOfferIdsForCardFeatureAndIssuerFilter({
    offerIdsForCardFeatureFilter,
    selectedIssuers,
    offersByIssuerMap,
  });

  // Included offers in selected both Card Feature and Credit Range filters
  const offerIdsForCardFeatureAndCreditRangeFilter = getOfferIdsForCardFeatureAndCreditRangeFilter({
    offerIdsForCardFeatureFilter,
    selectedCreditRangeFilter,
    offersByRCSMap,
  });

  // Calculate the number of offers for each issuer according to the selected Card Feature and Credit Range filters
  allIssuerIds.forEach(issuerId => {
    const issuerOffersIds = offersByIssuerMap[issuerId];
    issuerOffersIds.forEach(offerId => {
      if (offerIdsForCardFeatureAndCreditRangeFilter.has(offerId)) {
        availableOffersCount.issuers[issuerId] += 1;
      }
    });
    if (selectedIssuers.includes(issuerId) && availableOffersCount.issuers[issuerId] === 0) {
      filtersToCleanup.issuerIdsToCleanup.push(issuerId);
    }
  });

  const rcsOptions = Object.keys(offersByRCSMap) as CreditRangeEnum[];

  // Calculate the number of offers for each credit range according to the selected Card Feature and Issuer filters
  rcsOptions.forEach(creditRange => {
    offerIdsForCardFeatureAndIssuerFilter.forEach(offerId => {
      if (offersByRCSMap[creditRange]?.has(offerId)) {
        availableOffersCount.creditRange[creditRange] += 1;
      }
    });
    if (
      selectedCreditRangeFilter.includes(creditRange) &&
      availableOffersCount.creditRange[creditRange] === 0
    ) {
      filtersToCleanup.creditRangesToCleanup.push(creditRange);
    }
  });

  if (
    filtersToCleanup.issuerIdsToCleanup.length === 0 &&
    filtersToCleanup.creditRangesToCleanup.length === 0
  ) {
    // If no filters need to be cleaned up, return the available offers count and cleaned filters
    return { availableOffersCount, cleanedFilters };
  }

  // Recursively call the function to cleanup the filters and recalculate the available offers count
  // This recursion is not a problem because it runs twice tops and only on edge cases
  return cleanupFiltersAndGetAvailableOffersCount({
    preComputedFilterOfferIds,
    newSelectedFilters: cleanedFilters,
    filtersToCleanup,
  });
};

export const getLabelForFilterItem = ({
  type,
  item,
  issuersIdNameMap,
}: {
  type: FilterType;
  item: string;
  issuersIdNameMap: PreComputedFilterOfferIds['issuersIdNameMap'];
}) => {
  let label;
  switch (type) {
    case FilterType.CARD_FEATURE:
      label = CARD_FEATURES_OPTIONS_LABEL_MAP[item as FilterOffersByType];
      break;
    case FilterType.ISSUER:
      label = issuersIdNameMap[item];
      break;
    case FilterType.CREDIT_RANGE:
      label = CREDIT_RANGE_OPTIONS_LABEL_MAP[item as CreditRangeEnum];
      break;
    case FilterType.SORT_BY:
      label = SORT_BY_OPTIONS_LABEL_MAP[item as SortOffersByType];
      break;
  }
  if (!label) {
    if (typeof window !== 'undefined') {
      import('@datadog/browser-rum').then(({ datadogRum }) => {
        datadogRum.addError(new Error(`Label not found for type: ${type} and value: ${item}`));
      });
    } else {
      throw new Error(`Label not found for type: ${type} and value: ${item}`);
    }
  }
  return label;
};

const MULTI_SELECT_FILTERS = [FilterType.ISSUER, FilterType.CREDIT_RANGE];

export const getNonDefaultSelectedFilters = ({
  newSelectedFilters,
  issuersIdNameMap,
}: {
  newSelectedFilters: FilterState;
  issuersIdNameMap: PreComputedFilterOfferIds['issuersIdNameMap'];
}) => {
  const selectedNonDefaultFilters = (
    Object.entries(newSelectedFilters) as [FilterType, FilterState[FilterType]][]
  ).reduce((acc: SelectedNonDefaultFilters[], [key, value]) => {
    if (!MULTI_SELECT_FILTERS.includes(key)) {
      return acc;
    }
    if (Array.isArray(value) && value.length > 0) {
      value.forEach(item => {
        acc.push({
          type: key,
          label: getLabelForFilterItem({
            type: key,
            item,
            issuersIdNameMap,
          }),
          value: item,
        });
      });
    }
    return acc;
  }, []);
  return selectedNonDefaultFilters;
};
